<template>
  <div class="w-full h-full space-y-4">
    <Card>
<CardHeader class="space-y-4">
  <div>
    <CardTitle >
      Boogi Intelligence
    </CardTitle>
    <CardDescription class="mt-2">
      Smarter insights. Stronger engagement. More rewarding transactions.
    </CardDescription>
  </div>

  <div class="rounded-xl border bg-muted/30 p-6">
    <div class="flex items-center gap-3 mb-4">
      <div class="flex h-8 w-8 items-center justify-center rounded-xl ">
        <BrainCircuit class="h-8 w-8 text-primary" />
      </div>

      <div>
        <h3 class="">
          Meet REUBEN
        </h3>
        <p class="text-sm text-muted-foreground">
          Recommendation Engine for User Behaviour, Engagement & Nudges
        </p>
      </div>
    </div>

    <p class="text-sm leading-relaxed text-muted-foreground">
      REUBEN is the underlying technology that powers Boogi's
      intelligence. By analysing customer transaction behaviour,
      REUBEN identifies missed engagement opportunities and transforms
      everyday spending activity into powerful revenue generating recommendations.
    </p>
  </div>
</CardHeader>
      <CardContent class="space-y-4">
        <div class="flex flex-wrap items-end gap-3">
          <div class="space-y-1"><label class="text-xs text-muted-foreground" for="recommendation-start">Start date</label><Input id="recommendation-start" v-model="startDate" type="date" /></div>
          <div class="space-y-1"><label class="text-xs text-muted-foreground" for="recommendation-end">End date</label><Input id="recommendation-end" v-model="endDate" type="date" /></div>
          <Button :disabled="loading" @click="applyDates">Apply dates</Button>
          <Button variant="outline" :disabled="loading" @click="clearDates">Clear dates</Button>
        </div>
        <p v-if="dateError" role="alert" class="text-sm text-destructive">{{ dateError }}</p>
        <DataTable
          :headers="headers" :items="recommendations" :loading="loading"
          v-model:server-options="serverOptions" :server-items-length="serverItemsLength"
          searchable search-placeholder="Search by recommendation ID or user ID"
          :search="search" @update:search="search = $event"
          @search="runSearch" @clear-search="clearSearch"
          :error="error" @retry="loadRecommendations"
        >
          <template #item-id="row"><CopyId v-if="row.id" :value="row.id" :max-width="140" /><span v-else>—</span></template>
          <template #item-retailer="row">
            <div v-if="row.programOffer?.retailer" class="flex items-center gap-2">
              <img v-if="row.programOffer.retailer.logo" :src="row.programOffer.retailer.logo" alt="" class="w-6 h-6 rounded object-contain border bg-white" />
              <span class="text-sm font-medium">{{ row.programOffer.retailer.retailerName || '—' }}</span>
            </div>
            <span v-else class="text-sm text-muted-foreground">—</span>
          </template>
          <template #item-recommendationType="row"><Badge variant="secondary">{{ formatEnum(row.recommendationType) }}</Badge></template>
          <template #item-endUserId="row">
            <router-link v-if="row.endUserId" :to="`/enrolled-users/users/${encodeURIComponent(row.endUserId)}`" class="text-xs font-mono text-sky-600 dark:text-sky-400 hover:underline" @click.stop>{{ row.endUserId }}</router-link>
            <span v-else>—</span>
          </template>
          <template #item-transactionData="row"><span class="text-xs">{{ transactionLabel(row.transactionData) }}</span></template>
          <template #item-payoutData="row"><span class="text-xs">{{ payoutLabel(row.payoutData) }}</span></template>
          <template #item-createdOn="row"><span class="text-xs whitespace-nowrap">{{ niceDateTime(row.createdOn) }}</span></template>
          <template #item-updatedOn="row"><span class="text-xs whitespace-nowrap">{{ niceDateTime(row.updatedOn) }}</span></template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge, Input, CopyId, DataTable } from '@boogi/ui'
import { BrainCircuit } from 'lucide-vue-next'
export default {
  name: 'BoogiIntelligence',
  components: { BrainCircuit, Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Badge, Input, CopyId, DataTable },
  data() {
    return {
      recommendations: [], loading: false, error: false, dateError: '',
      search: '', activeSearch: '', startDate: '', endDate: '',
      appliedStartDate: '', appliedEndDate: '',
      serverOptions: { page: 1, rowsPerPage: 25 }, serverItemsLength: 0,
      headers: [
        { text: 'Retailer', value: 'retailer' },
        { text: 'Recommendation ID', value: 'id' },
        { text: 'Type', value: 'recommendationType' },
        { text: 'User', value: 'endUserId' },
        { text: 'Transaction', value: 'transactionData' },
        { text: 'Potential cashback', value: 'payoutData' },
        { text: 'Date created', value: 'createdOn' },
        { text: 'Last updated', value: 'updatedOn' },
      ],
    }
  },
  created() {
    this.search = String(this.$route.query.search || '').trim()
    this.activeSearch = this.search
    this.loadRecommendations()
  },
  mounted() { document.title = 'Dashboard | Boogi Intelligence' },
  watch: {
    serverOptions: {
      deep: true,
      handler() { this.loadRecommendations() },
    },
  },
  methods: {
    formatEnum(value) {
      if (!value) return '—'
      return String(value).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
    },
    niceDateTime(value) {
      if (value == null) return '—'
      const date = typeof value === 'number' ? DateTime.fromMillis(value) : DateTime.fromISO(String(value))
      return date.isValid ? date.toLocaleString(DateTime.DATETIME_MED) : '—'
    },
    formatMoney(amount, currency) {
      if (amount == null) return '—'
      try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount) }
      catch { return `${currency || ''} ${amount}`.trim() }
    },
    // These nested structures were not provided. Show values only when their shape matches known ledger fields.
    transactionLabel(data) {
      if (!data) return '—'
      const amount = data.transactionAmount || data.amount
      if (typeof amount === 'number') return this.formatMoney(amount, data.currency)
      if (amount && typeof amount.amount === 'number') return this.formatMoney(amount.amount, amount.currency)
      return data.transactionId || data.id || '—'
    },
    payoutLabel(data) {
      if (!data) return '—'
      const amount = data.endUserCashbackReceivable ?? data.cashbackAmount
      return this.formatMoney(amount, data.currency)
    },
    applyDates() {
      this.dateError = ''
      if (Boolean(this.startDate) !== Boolean(this.endDate)) {
        this.dateError = 'Provide both a start date and an end date.'
        return
      }
      if (this.startDate && this.startDate > this.endDate) {
        this.dateError = 'Start date must not be after end date.'
        return
      }
      this.appliedStartDate = this.startDate
      this.appliedEndDate = this.endDate
      this.serverOptions.page = 1
      this.loadRecommendations()
    },
    clearDates() {
      this.startDate = this.endDate = this.appliedStartDate = this.appliedEndDate = this.dateError = ''
      this.serverOptions.page = 1
      this.loadRecommendations()
    },
    runSearch() {
      this.activeSearch = this.search.trim()
      this.serverOptions.page = 1
      this.loadRecommendations()
    },
    clearSearch() {
      this.search = this.activeSearch = ''
      this.serverOptions.page = 1
      this.loadRecommendations()
    },
    async loadRecommendations() {
      this.loading = true
      this.error = false
      try {
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const base = `${apiUrl}/private/api/client/recommendations`
        const term = this.activeSearch
        const page = this.serverOptions.page - 1
        const size = this.serverOptions.rowsPerPage
        const params = new URLSearchParams({ page: String(page), size: String(size) })
        if (this.appliedStartDate && this.appliedEndDate) {
          params.set('startDate', this.appliedStartDate)
          params.set('endDate', this.appliedEndDate)
        }
        const headers = { 'Content-Type': 'application/json', Authorization: localStorage.getItem('token') }
        let data
        if (term) {
          // Search the user endpoint and the ID endpoint; the API has no generic text search.
          const userUrl = `${base}/user/${encodeURIComponent(term)}?${params}`
          const idUrl = `${base}/${encodeURIComponent(term)}`
          const responses = await Promise.allSettled([userUrl, idUrl].map(url => this.$apiFetch(url, { method: 'GET', headers })))
          const matches = new Map()
          let successful = false
          for (const result of responses) {
            if (result.status !== 'fulfilled' || !result.value.ok) continue
            successful = true
            const body = await result.value.json()
            for (const item of (Array.isArray(body.content) ? body.content : body?.id ? [body] : [])) {
              if (item?.id) matches.set(item.id, item)
            }
          }
          if (!successful) throw new Error('Recommendation search failed')
          data = { content: [...matches.values()], totalElements: matches.size }
        } else {
          const response = await this.$apiFetch(`${base}?${params}`, { method: 'GET', headers })
          if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`)
          data = await response.json()
        }
        this.recommendations = data.content || []
        this.serverItemsLength = data.totalElements ?? this.recommendations.length
      } catch (e) {
        this.error = true
        this.recommendations = []
        this.serverItemsLength = 0
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
